/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICoreContext, IGeneric, IAny } from '@msdyn365-commerce/core';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { ICheckoutCustomerAccountPaymentViewProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/./checkout-customer-account-payment';
import { IAccountPaymentEditViewForm } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/./components/get-account-payment-form-edit-mode';
import { IAccountPaymentSummaryViewForm } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/./components/get-account-payment-form-summary-mode';
import { IModuleStateManager } from '@msdyn365-commerce-modules/checkout-utilities';
import { Customer, CustomerAttribute, CustomerBalances } from '@msdyn365-commerce/retail-proxy';
import {
    ICheckoutCustomerAccountPaymentProps,
    ICheckoutCustomerAccountPaymentResources
} from '../definition-extensions/checkout-customer-account-payment.ext.props.autogenerated';
import { currencyFormat } from '../../../utils/helper';

//VSI Customization
// extends IAccountPaymentEditViewForm
interface IAccountPaymentEditViewFormExtended extends IAccountPaymentEditViewForm {
    context: ICoreContext<IGeneric<IAny>>;
    resources: ICheckoutCustomerAccountPaymentResources;
    customerInfo?: Customer;
    checkoutInfo?: ICheckoutState;
    customerBalanceInfo?: CustomerBalances;
    moduleState: IModuleStateManager;
}

//VSI Customization
const _getPaymentAmount = (amount: React.ReactNode): string => {
    const amountJSX = amount as JSX.Element;
    const amountPropsJSX = amountJSX.props;
    return amountPropsJSX?.max || '0';
};

const _getAvailableCredit = (creditBalances: CustomerBalances | undefined) => {
    let availableCredit = 0;

    if (!creditBalances) {
        return 0;
    }

    if (creditBalances.InvoiceAccountCreditLimit === 0) {
        availableCredit = creditBalances.CreditLimit - creditBalances.Balance - creditBalances.PendingBalance;
    } else {
        availableCredit =
            creditBalances.InvoiceAccountCreditLimit - creditBalances.InvoiceAccountBalance - creditBalances.InvoiceAccountPendingBalance;
    }

    return availableCredit;
};

const _addPayment = async (paymentAmount: number, checkoutState?: ICheckoutState): Promise<void> => {
    if (!checkoutState) {
        return;
    }

    await checkoutState.updateCustomerAccountAmount({ newAmount: paymentAmount });
};
export const SummaryForm: React.FC<IAccountPaymentSummaryViewForm> = ({
    formProps,
    label,
    paymentAmount,
    addPaymentButton,
    appliedLine,
    bottomBorder
}) => (
    <Node {...formProps}>
        <>
            {label}
            {paymentAmount}
            {addPaymentButton}
            {appliedLine}
            {bottomBorder}
        </>
    </Node>
);

export const EditForm: React.FC<IAccountPaymentEditViewFormExtended> = ({
    formProps,
    inputLabel,
    inputAmount,
    addPaymentButton,
    customerName,
    customerAccountNumber,
    customerSince,
    accountCredit,
    alert,
    accountDetails,
    appliedLine,
    bottomBorder,
    resources,
    context,
    customerBalanceInfo,
    customerInfo,
    checkoutInfo,
    moduleState
}) => {
    const customerAttributes: CustomerAttribute[] = customerInfo?.Attributes || [];

    const isPrePaidCustomer =
        customerAttributes.find(attr => attr.Name === context.app.config.prepaidCustomerAttribute || 'Prepaid Customer')?.AttributeValue
            ?.BooleanValue || false;

    const creditBalance = customerBalanceInfo ? _getAvailableCredit(customerBalanceInfo) : 0;

    const paymentAmount = _getPaymentAmount(inputAmount);
    const usSiteFlag = context?.actionContext?.requestContext?.apiSettings?.oun === context.app.config.ounNumberForUsSite;

    React.useEffect(() => {
        const _addPaymentHandler = async (amountValue: number) => {
            await _addPayment(amountValue, checkoutInfo);
        };

        if (isPrePaidCustomer) {
            _addPaymentHandler(Number(paymentAmount)).then(() => {
                moduleState.onReady();
            });
        } else {
            _addPaymentHandler(Number(paymentAmount)).then(() => {
                moduleState.onReady();
            });
        }
    }, [paymentAmount]);

    return (
        <Node {...formProps}>
            <>
                {usSiteFlag ? (
                    <></>
                ) : isPrePaidCustomer ? (
                    <p className='prepaid-message'>{resources.prepaidCustomerMessage}</p>
                ) : (
                    <>
                        {false && customerName}
                        {false && customerAccountNumber}
                        {false && <div className='applied-line-container'>{customerSince}</div>}
                        {false && accountCredit}
                        {false && accountDetails}
                        {false && inputLabel}
                        {false && alert}
                        {false && inputAmount}
                        {false && addPaymentButton}
                        {false && appliedLine}
                        {false && bottomBorder}
                        {false && (
                            <div className='total-limit-container'>
                                <span className='total-limit-title'>{resources.totalAvailableCreditLimit} </span>
                                <span className='total-limit-amount'>{currencyFormat(creditBalance, context)}</span>
                            </div>
                        )}
                        <div className='credit-limit-input-container'>
                            <span className='amount-label'>{resources.orderAmount}</span>
                            <span className='amount-value'>{currencyFormat(Number(paymentAmount), context)}</span>
                        </div>
                    </>
                )}
            </>
        </Node>
    );
};

const CheckoutCustomerAccountView: React.FC<ICheckoutCustomerAccountPaymentViewProps &
    ICheckoutCustomerAccountPaymentProps<{}>> = props => {
    const {
        checkoutCustomerAccount,
        summaryView,
        editView,
        moduleState,
        data: {
            customerInformation: { result: customerInfo },
            creditBalances: { result: customerBalanceInfo },
            checkout: { result: checkoutInfo }
        },
        context,
        resources,
        checkoutErrorRef
    } = props;
    return (
        <Module {...checkoutCustomerAccount} ref={checkoutErrorRef}>
            {alert}

            {false && moduleState.isReady && summaryView && <SummaryForm {...summaryView} />}
            {/* {!moduleState.isReady && editView && <EditForm {...editView} />} */}
            {/* VSI CUSTOMIZATION START */}
            <EditForm
                context={context}
                resources={resources}
                customerInfo={customerInfo}
                customerBalanceInfo={customerBalanceInfo}
                checkoutInfo={checkoutInfo}
                moduleState={moduleState}
                {...editView}
            />

            {/* VSI CUSTOMIZATION END */}
        </Module>
    );
};

export default CheckoutCustomerAccountView;
